import React, { useCallback, useEffect, useState } from 'react';
import { PageHeader, Card, DataTable, Button, Badge, Tabs, SearchInput, Toast } from '../ui';
import Drawer from './Drawer';
import {
  listSourceMappings,
  upsertSourceMapping,
  deleteSourceMapping,
  listDestinationMappings,
  upsertDestinationMapping,
  deleteDestinationMapping,
  previewMapping,
  listAttributes,
} from '../../services/pimApi';
import './pim.css';

const SCOPE_STRATEGIES = ['global', 'channel_locale', 'channel', 'locale'];
const FIELD_KINDS = ['core', 'custom_field', 'category', 'price'];

const emptySource = {
  source_system: 'akeneo',
  source_field: '',
  target_attribute_code: '',
  scope_strategy: 'global',
  transform: 'identity',
  value_lookup_id: '',
  protect_when_enriched: false,
  enabled: true,
};

const emptyDest = {
  destination_system: 'bigcommerce',
  field_kind: 'core',
  destination_field: '',
  source_attribute_code: '',
  source_alias: '',
  market_scoped: false,
  transform: 'identity',
  value_lookup_id: '',
  forced_value: '',
  enabled: true,
};

function MappingForm({ kind, draft, setDraft, attributes }) {
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  return (
    <>
      {kind === 'source' ? (
        <>
          <div className="pim-field">
            <label>Source system</label>
            <input type="text" value={draft.source_system} onChange={(e) => set('source_system', e.target.value)} />
          </div>
          <div className="pim-field">
            <label>Source field</label>
            <input type="text" value={draft.source_field} onChange={(e) => set('source_field', e.target.value)} placeholder="e.g. product_details.product_name" />
          </div>
          <div className="pim-field">
            <label>Target attribute</label>
            <select value={draft.target_attribute_code} onChange={(e) => set('target_attribute_code', e.target.value)}>
              <option value="">Select attribute…</option>
              {attributes.map((a) => (
                <option key={a.code} value={a.code}>{a.label} ({a.code})</option>
              ))}
            </select>
          </div>
          <div className="pim-field">
            <label>Scope strategy</label>
            <select value={draft.scope_strategy} onChange={(e) => set('scope_strategy', e.target.value)}>
              {SCOPE_STRATEGIES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <label className="pim-check" style={{ marginBottom: 16 }}>
            <input type="checkbox" checked={draft.protect_when_enriched} onChange={(e) => set('protect_when_enriched', e.target.checked)} />
            Protect when enriched (never overwrite manual enrichment)
          </label>
        </>
      ) : (
        <>
          <div className="pim-field">
            <label>Destination system</label>
            <input type="text" value={draft.destination_system} onChange={(e) => set('destination_system', e.target.value)} />
          </div>
          <div className="pim-field">
            <label>Field kind</label>
            <select value={draft.field_kind} onChange={(e) => set('field_kind', e.target.value)}>
              {FIELD_KINDS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="pim-field">
            <label>Destination field</label>
            <input type="text" value={draft.destination_field} onChange={(e) => set('destination_field', e.target.value)} placeholder="e.g. name, custom:Warranty" />
          </div>
          <div className="pim-field">
            <label>Source attribute</label>
            <select value={draft.source_attribute_code} onChange={(e) => set('source_attribute_code', e.target.value)}>
              <option value="">(use alias / forced value)</option>
              {attributes.map((a) => (
                <option key={a.code} value={a.code}>{a.label} ({a.code})</option>
              ))}
            </select>
          </div>
          <div className="pim-field">
            <label>Source alias (display_*)</label>
            <input type="text" value={draft.source_alias} onChange={(e) => set('source_alias', e.target.value)} placeholder="e.g. display_name" />
          </div>
          <div className="pim-field">
            <label>Forced value (JSON, optional)</label>
            <input type="text" value={draft.forced_value} onChange={(e) => set('forced_value', e.target.value)} placeholder='e.g. true or "fixed"' />
          </div>
          <label className="pim-check" style={{ marginBottom: 16 }}>
            <input type="checkbox" checked={draft.market_scoped} onChange={(e) => set('market_scoped', e.target.checked)} />
            Market-scoped (resolve per region)
          </label>
        </>
      )}
      <div className="pim-field">
        <label>Transform</label>
        <input type="text" value={draft.transform} onChange={(e) => set('transform', e.target.value)} placeholder="identity" />
        <div className="pim-field-hint">Name of a registered transform; defaults to identity.</div>
      </div>
      <div className="pim-field">
        <label>Value lookup id (optional)</label>
        <input type="number" value={draft.value_lookup_id} onChange={(e) => set('value_lookup_id', e.target.value)} />
      </div>
      <label className="pim-check">
        <input type="checkbox" checked={draft.enabled} onChange={(e) => set('enabled', e.target.checked)} />
        Enabled
      </label>
    </>
  );
}

export default function MappingsAdmin() {
  const [tab, setTab] = useState('source');
  const [sources, setSources] = useState([]);
  const [dests, setDests] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [editing, setEditing] = useState(null); // {kind, draft}
  const [busy, setBusy] = useState(false);

  // Preview tool
  const [previewSku, setPreviewSku] = useState('');
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, d, a] = await Promise.all([listSourceMappings(), listDestinationMappings(), listAttributes()]);
      setSources(s);
      setDests(d);
      setAttributes(a);
    } catch (e) {
      setToast({ message: 'Failed to load mappings', error: true });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing({ kind: tab, draft: tab === 'source' ? { ...emptySource } : { ...emptyDest } });
  };

  const openEdit = (kind, row) => {
    const draft = kind === 'source'
      ? {
          id: row.id,
          source_system: row.source_system,
          source_field: row.source_field,
          target_attribute_code: row.target_attribute_code,
          scope_strategy: row.scope_strategy,
          transform: row.transform || 'identity',
          value_lookup_id: row.value_lookup_id ?? '',
          protect_when_enriched: row.protect_when_enriched,
          enabled: row.enabled,
        }
      : {
          id: row.id,
          destination_system: row.destination_system,
          field_kind: row.field_kind,
          destination_field: row.destination_field,
          source_attribute_code: row.source_attribute_code || '',
          source_alias: row.source_alias || '',
          market_scoped: row.market_scoped,
          transform: row.transform || 'identity',
          value_lookup_id: row.value_lookup_id ?? '',
          forced_value: row.forced_value != null ? JSON.stringify(row.forced_value) : '',
          enabled: row.enabled,
        };
    setEditing({ kind, draft });
  };

  const save = async () => {
    const { kind, draft } = editing;
    setBusy(true);
    try {
      if (kind === 'source') {
        const payload = {
          ...draft,
          value_lookup_id: draft.value_lookup_id === '' ? null : Number(draft.value_lookup_id),
        };
        await upsertSourceMapping(payload);
      } else {
        let forced = null;
        if (draft.forced_value !== '') {
          try {
            forced = JSON.parse(draft.forced_value);
          } catch {
            forced = draft.forced_value; // treat as raw string
          }
        }
        const payload = {
          ...draft,
          source_attribute_code: draft.source_attribute_code || null,
          source_alias: draft.source_alias || null,
          value_lookup_id: draft.value_lookup_id === '' ? null : Number(draft.value_lookup_id),
          forced_value: forced,
        };
        await upsertDestinationMapping(payload);
      }
      setEditing(null);
      setToast({ message: 'Mapping saved' });
      await load();
    } catch (e) {
      setToast({ message: e?.response?.data?.detail || 'Save failed', error: true });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (kind, id) => {
    try {
      if (kind === 'source') await deleteSourceMapping(id);
      else await deleteDestinationMapping(id);
      await load();
      setToast({ message: 'Mapping deleted' });
    } catch (e) {
      setToast({ message: 'Delete failed', error: true });
    }
  };

  const runPreview = async () => {
    if (!previewSku.trim()) return;
    try {
      setPreview(await previewMapping(previewSku.trim()));
    } catch (e) {
      setToast({ message: e?.response?.data?.detail || 'Preview failed', error: true });
      setPreview(null);
    }
  };

  const sourceCols = [
    { key: 'source_system', header: 'System' },
    { key: 'source_field', header: 'Source field', render: (r) => <code>{r.source_field}</code> },
    { key: 'target', header: 'Target attribute', render: (r) => `${r.target_attribute_label} (${r.target_attribute_code})` },
    { key: 'scope_strategy', header: 'Scope' },
    { key: 'transform', header: 'Transform' },
    { key: 'flags', header: 'Flags', render: (r) => (
        <span style={{ display: 'flex', gap: 6 }}>
          {r.protect_when_enriched && <Badge variant="warning">protected</Badge>}
          {r.enabled ? <Badge variant="success">on</Badge> : <Badge variant="neutral">off</Badge>}
        </span>
      ) },
    { key: 'actions', header: '', align: 'right', render: (r) => (
        <span style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          <Button size="sm" variant="secondary" onClick={() => openEdit('source', r)}>Edit</Button>
          <Button size="sm" variant="danger" onClick={() => remove('source', r.id)}>Delete</Button>
        </span>
      ) },
  ];

  const destCols = [
    { key: 'destination_system', header: 'System' },
    { key: 'field_kind', header: 'Kind' },
    { key: 'destination_field', header: 'Destination field', render: (r) => <code>{r.destination_field}</code> },
    { key: 'source', header: 'Source', render: (r) => r.source_attribute_code || r.source_alias || (r.forced_value != null ? `=${JSON.stringify(r.forced_value)}` : '—') },
    { key: 'transform', header: 'Transform' },
    { key: 'flags', header: 'Flags', render: (r) => (
        <span style={{ display: 'flex', gap: 6 }}>
          {r.market_scoped && <Badge variant="info">per-region</Badge>}
          {r.enabled ? <Badge variant="success">on</Badge> : <Badge variant="neutral">off</Badge>}
        </span>
      ) },
    { key: 'actions', header: '', align: 'right', render: (r) => (
        <span style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          <Button size="sm" variant="secondary" onClick={() => openEdit('destination', r)}>Edit</Button>
          <Button size="sm" variant="danger" onClick={() => remove('destination', r.id)}>Delete</Button>
        </span>
      ) },
  ];

  return (
    <>
      <PageHeader
        title="PIM Mappings"
        sub="Inbound source→attribute and outbound attribute→destination mapping rules."
        actions={<Button onClick={openNew}>+ New {tab === 'source' ? 'inbound' : 'outbound'} mapping</Button>}
      />

      <Tabs
        tabs={[
          { id: 'source', label: 'Inbound (source → PIM)' },
          { id: 'destination', label: 'Outbound (PIM → destination)' },
          { id: 'preview', label: 'Preview' },
        ]}
        active={tab}
        onChange={setTab}
        label="Mapping direction"
      />

      {tab === 'source' && (
        <Card flush>
          <DataTable columns={sourceCols} rows={sources} rowKey={(r) => r.id} loading={loading} />
        </Card>
      )}

      {tab === 'destination' && (
        <Card flush>
          <DataTable columns={destCols} rows={dests} rowKey={(r) => r.id} loading={loading} />
        </Card>
      )}

      {tab === 'preview' && (
        <Card title="Resolve a SKU" desc="Dry-run the outbound mappings against a product's EAV values.">
          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            <SearchInput value={previewSku} onChange={setPreviewSku} placeholder="Enter SKU…" />
            <Button onClick={runPreview}>Resolve</Button>
          </div>
          {preview && (
            <div className="pim-preview">
              {Object.entries(preview.payloads).map(([market, payload]) => (
                <div key={market} className="pim-preview-block">
                  <h4>{market}</h4>
                  <pre>{JSON.stringify(payload, null, 2)}</pre>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {editing && (
        <Drawer
          title={`${editing.draft.id ? 'Edit' : 'New'} ${editing.kind === 'source' ? 'inbound' : 'outbound'} mapping`}
          onClose={() => setEditing(null)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setEditing(null)} disabled={busy}>Cancel</Button>
              <Button onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
            </>
          }
        >
          <MappingForm
            kind={editing.kind}
            draft={editing.draft}
            setDraft={(updater) => setEditing((e) => ({ ...e, draft: typeof updater === 'function' ? updater(e.draft) : updater }))}
            attributes={attributes}
          />
        </Drawer>
      )}

      <Toast message={toast?.message} error={toast?.error} />
    </>
  );
}
